import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import LabelWithInput from "@components/input/LabelWithInput";
import FadeInContainer from "@components/motion/FadeInContainer";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { CategoryDropdownInput } from "@pages/register/_components/index";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
}));

const CategoryInput = ({ nonAnimation }: { nonAnimation?: boolean }) => {
  const [focusName, setFocusName] = useState<string>("");
  const { getCategoryList } = useApiRequest();
  const {
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();

  const { pathname } = useLocation();
  const isProductEdit = pathname === "/product-edit";

  const depth1 = watch("depth1");
  const depth2 = watch("depth2");
  const depth3 = watch("depth3");

  const name_ko = watch("name_ko");
  const brand_id = watch("brand_id");

  useEffect(() => {
    if (depth1) {
      trigger("depth1");
    }
  }, [depth1, trigger]);

  // depth1 카테고리 불러오기
  const { data: depth1List } = useQuery({
    queryKey: ["depth1List"],
    queryFn: () => getCategoryList(),
  });

  const { data: depth2List } = useQuery({
    queryKey: ["depth2List", depth1],
    queryFn: () => getCategoryList(depth1),
    enabled: !!depth1,
  });

  const { data: depth3List } = useQuery({
    queryKey: ["depth3List", depth2],
    queryFn: () => getCategoryList(depth2),
    enabled: !!depth2,
  });

  const { data: depth4List } = useQuery({
    queryKey: ["depth4List", depth3],
    queryFn: () => getCategoryList(depth3),
    enabled: !!depth3,
  });

  const { t } = useTranslation();

  return (
    <Container>
      {/* depth1 카테고리 */}

      <FadeInContainer
        isShow={brand_id !== null && !!name_ko}
        nonAnimation={isProductEdit}>
        <LabelWithInput label="카테고리" required>
          <CategoryDropdownInput
            focusName={focusName}
            setFocusName={setFocusName}
            options={depth1List || []}
            name="depth1"
          />

          {errors.depth1 && errors.depth1.message && (
            <ErrorMessage>
              {typeof errors.depth1.message === "string"
                ? t(errors.depth1.message)
                : t("")}
            </ErrorMessage>
          )}
        </LabelWithInput>
      </FadeInContainer>

      {/* depth2 카테고리 */}
      <FadeInContainer isShow={!!depth1}>
        <CategoryDropdownInput
          focusName={focusName}
          setFocusName={setFocusName}
          options={depth2List || []}
          name="depth2"
        />
      </FadeInContainer>

      {/* depth3 카테고리 */}
      <FadeInContainer isShow={!!depth2}>
        <CategoryDropdownInput
          focusName={focusName}
          setFocusName={setFocusName}
          options={depth3List || []}
          name="depth3"
        />
      </FadeInContainer>

      {/* depth4 카테고리 */}
      <FadeInContainer isShow={!!depth3}>
        <CategoryDropdownInput
          focusName={focusName}
          setFocusName={setFocusName}
          options={depth4List || []}
          name="depth4"
        />
      </FadeInContainer>
    </Container>
  );
};

export default CategoryInput;
